import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, Save, X, ShieldCheck } from 'lucide-react';
import { confirm } from '../components/ui/confirm-dialog';

const PROTECTED_ROLE = 'Super Admin';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/* ============================================================
 * Canonical module access matrix.
 *
 * Each module has an ordered list of access levels; each level maps to a
 * concrete set of permission codenames the role should receive.
 *
 * Backend must have these codenames in the `permissions` table (seeded at
 * startup). Selecting "NO VIEW" gives the role no perms for that module.
 * ============================================================ */
const MODULES = [
  {
    key: 'dashboard',
    label: 'Dashboard & Reports',
    description: 'The dashboard and all report screens.',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['dashboard.view'] },
    ],
  },
  {
    key: 'orders_kitchen',
    label: 'Kitchen View',
    description: 'The kitchen order board (KDS) — accept, cook, mark ready.',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['orders.view'] },
    ],
  },
  {
    key: 'orders_manage',
    label: 'Order Management',
    description: 'Manage Orders / Active / All Orders (the billing side).',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['orders.manage'] },
    ],
  },
  {
    key: 'dishes',
    label: 'Dishes',
    description: 'All Dishes — View includes ingredients & recipe (for the kitchen).',
    levels: [
      { value: 'NONE',      label: 'No View',     perms: [] },
      { value: 'VIEW',      label: 'View',        perms: ['dishes.view'] },
      { value: 'VIEW_EDIT', label: 'View + Edit', perms: ['dishes.view', 'dishes.edit'] },
    ],
  },
  {
    key: 'menus',
    label: 'Menus & Buffets',
    description: 'All Menus and Open Buffet management.',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['menus.view'] },
    ],
  },
  {
    key: 'inventory',
    label: 'Inventory',
    description: 'Item tracking, purchases, bar & liquor screens.',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['inventory.view'] },
    ],
  },
  {
    key: 'tables',
    label: 'Tables',
    description: 'Tables, reservations and table QR codes.',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['tables.view'] },
    ],
  },
  {
    key: 'customers',
    label: 'Customers',
    description: 'Customer directory and CRM screens.',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['customers.view'] },
    ],
  },
  {
    key: 'staff',
    label: 'Staff',
    description: 'Staff, roles and session screens.',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['staff.view'] },
    ],
  },
  {
    key: 'settings_other',
    label: 'Other Settings',
    description: 'Tax rates, menu view and display settings.',
    levels: [
      { value: 'NONE', label: 'No View', perms: [] },
      { value: 'VIEW', label: 'View',    perms: ['settings.view'] },
    ],
  },
  {
    key: 'company',
    label: 'Company',
    description: 'Company Profile pages under Settings.',
    levels: [
      { value: 'NONE',      label: 'No View',      perms: [] },
      { value: 'VIEW',      label: 'View',         perms: ['company.view'] },
      { value: 'VIEW_EDIT', label: 'View + Edit',  perms: ['company.view', 'company.edit'] },
    ],
  },
  {
    key: 'locations',
    label: 'Locations',
    description: 'Restaurant groups (franchises / brands) and their locations / branches.',
    levels: [
      { value: 'NONE',      label: 'No View',      perms: [] },
      { value: 'VIEW',      label: 'View',         perms: ['locations.view'] },
      { value: 'VIEW_EDIT', label: 'View + Edit',  perms: ['locations.view', 'locations.edit'] },
      { value: 'CREATE',    label: 'Create',       perms: ['locations.view', 'locations.edit', 'locations.create'] },
    ],
  },
  {
    key: 'data_display',
    label: 'Data Display',
    description: 'Date/time/number/format display preferences.',
    levels: [
      { value: 'NONE',      label: 'No View', perms: [] },
      { value: 'VIEW',      label: 'View',    perms: ['data_display.view'] },
      { value: 'EDIT',      label: 'Edit',    perms: ['data_display.view', 'data_display.edit'] },
    ],
  },
  {
    key: 'master_data',
    label: 'Master Data',
    description: 'Countries, currencies, languages, restaurant/company types.',
    levels: [
      { value: 'NONE',      label: 'No View',     perms: [] },
      { value: 'VIEW',      label: 'View',        perms: ['master_data.view'] },
      { value: 'VIEW_EDIT', label: 'View + Edit', perms: ['master_data.view', 'master_data.edit'] },
      { value: 'CREATE',    label: 'Create',      perms: ['master_data.view', 'master_data.edit', 'master_data.create'] },
    ],
  },
];

/** Given the perm codes a role currently has, return the level for each module. */
function inferLevels(rolePermCodes) {
  const codes = new Set(rolePermCodes || []);
  const out = {};
  for (const m of MODULES) {
    // The "best matching" level is the one whose perm set is the maximum subset
    // of what the role has. Iterate descending so highest level wins.
    const sorted = [...m.levels].reverse();
    let chosen = 'NONE';
    for (const lvl of sorted) {
      if (lvl.perms.every((c) => codes.has(c))) {
        chosen = lvl.value;
        break;
      }
    }
    out[m.key] = chosen;
  }
  return out;
}

/** From level selection map → flat list of permission codenames the role should have. */
function levelsToCodes(levelByModule) {
  const out = new Set();
  for (const m of MODULES) {
    const level = m.levels.find((l) => l.value === levelByModule[m.key]) || m.levels[0];
    for (const c of level.perms) out.add(c);
  }
  return Array.from(out);
}

/* ---------------------- small ui helpers ---------------------- */

const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring';

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-800 mb-1.5">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
function TextInput({ value, onChange, ...rest }) {
  return <input className={inputCls} value={value ?? ''} onChange={onChange} {...rest} />;
}
function Btn({ kind = 'primary', children, className = '', ...rest }) {
  const styles = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    ghost:   'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50',
    danger:  'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  };
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm font-medium disabled:opacity-60 ${styles[kind]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}
function Modal({ title, onClose, children, footer, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className={`w-full ${wide ? 'max-w-3xl' : 'max-w-md'} rounded-lg bg-white shadow-xl`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-5 max-h-[75vh] overflow-y-auto">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-3 px-5 py-4 bg-slate-50 border-t border-slate-200 rounded-b-lg">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

function useToast() {
  const [toast, setToast] = useState(null);
  const show = (msg, kind = 'success') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2200);
  };
  return {
    show,
    el: toast && (
      <div
        className={`fixed bottom-6 right-6 px-4 py-2.5 rounded-md text-sm shadow-lg z-[60] ${
          toast.kind === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-success text-white'
        }`}
      >
        {toast.msg}
      </div>
    ),
  };
}

/* ============================================================
 * Main page — single Roles screen
 * ============================================================ */

export default function RolesManagement() {
  const toast = useToast();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rolesRes, permsRes] = await Promise.all([
        fetch(`${API_URL}/users/login/roles/`).then((r) => r.json()),
        fetch(`${API_URL}/users/login/permissions/`).then((r) => r.json()),
      ]);
      setRoles(rolesRes?.data?.roles || []);
      setPermissions(permsRes?.data || []);
    } catch (_) {
      toast.show('Failed to load roles', 'error');
    } finally {
      setLoading(false);
    }
  };
  // `load` is a stable async fetcher; we only want it on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Build a codename → id lookup so the editor can save by id.
  const codeToId = useMemo(() => {
    const m = {};
    permissions.forEach((p) => { m[p.codename] = p.id; });
    return m;
  }, [permissions]);

  const handleDelete = async (role) => {
    if (!(await confirm({ description: `Delete role "${role.name}"? This cannot be undone.`, confirmText: 'Delete role', tone: 'destructive' }))) return;
    const res = await fetch(`${API_URL}/users/login/roles/${role.id}/delete/`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('sessionToken') || ''}` },
    });
    const data = await res.json();
    if (data?.success) {
      toast.show('Role deleted', 'success');
      load();
    } else {
      toast.show(data?.error || 'Delete failed', 'error');
    }
  };

  /** Short summary text for the role row: e.g. "Company View · Locations No View · Data Display Edit · Master Data Create" */
  const summarizeAccess = (role) => {
    const codes = (role.permissions || []).map((p) => p.codename);
    const levels = inferLevels(codes);
    return MODULES.map((m) => {
      const lvl = m.levels.find((l) => l.value === levels[m.key]);
      return `${m.label}: ${lvl?.label || 'No View'}`;
    }).join(' · ');
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-slate-50">
      <div className="w-full p-6">
        <div className="mb-6">
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Roles</h1>
          <p className="text-sm text-slate-500 mt-1">
            Create a role and pick the access level for each module. Each level grants the right
            permission codes behind the scenes.
          </p>
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-600">
            {loading ? 'Loading…' : `${roles.length} role${roles.length === 1 ? '' : 's'} defined`}
          </p>
          <Btn onClick={() => setCreating(true)}>
            <Plus size={16} /> New Role
          </Btn>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-3 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <div className="col-span-3">Name</div>
            <div className="col-span-8">Access</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">Loading roles…</div>
          ) : roles.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">
              No roles yet. Click <strong>New Role</strong> to create one.
            </div>
          ) : (
            roles.map((r) => {
              const isProtected = r.name === PROTECTED_ROLE;
              return (
                <div key={r.id} className="grid grid-cols-12 px-4 py-3 border-t border-slate-100 text-sm text-slate-700 hover:bg-slate-50">
                  <div className="col-span-3">
                    <div className="font-medium text-slate-900 flex items-center gap-2">
                      {r.name}
                      {isProtected && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning-soft text-warning">
                          <ShieldCheck size={10} /> Protected
                        </span>
                      )}
                    </div>
                    {r.description && <div className="text-xs text-slate-500">{r.description}</div>}
                  </div>
                  <div className="col-span-8 text-xs text-slate-600">{summarizeAccess(r)}</div>
                  <div className="col-span-1 flex justify-end gap-1">
                    {isProtected ? (
                      <span className="text-[10px] italic text-slate-400">System role</span>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setEditing(r)}
                          className="p-1.5 text-muted-foreground hover:text-primary rounded"
                          title="Edit role"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(r)}
                          className="p-1.5 text-muted-foreground hover:text-destructive rounded"
                          title="Delete role"
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {(creating || editing) && (
          <RoleEditorModal
            role={editing}
            codeToId={codeToId}
            onClose={() => { setCreating(false); setEditing(null); }}
            onSaved={() => { setCreating(false); setEditing(null); load(); }}
            showToast={toast.show}
          />
        )}
      </div>
      {toast.el}
    </div>
  );
}

/* ============================================================
 * Role editor — name + 4-module access matrix
 * ============================================================ */

function RoleEditorModal({ role, codeToId, onClose, onSaved, showToast }) {
  const isEdit = !!role;
  const [name, setName] = useState(role?.name || '');
  const [description, setDescription] = useState(role?.description || '');
  const [levels, setLevels] = useState(() =>
    inferLevels((role?.permissions || []).map((p) => p.codename))
  );
  const [saving, setSaving] = useState(false);

  const setLevel = (moduleKey, levelValue) =>
    setLevels((prev) => ({ ...prev, [moduleKey]: levelValue }));

  const save = async () => {
    if (!name.trim()) {
      showToast('Role name is required', 'error');
      return;
    }
    setSaving(true);
    const authHeaders = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('sessionToken') || ''}`,
    };
    try {
      let roleId = role?.id;
      if (isEdit) {
        const res = await fetch(`${API_URL}/users/login/roles/${roleId}/update/`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ name, description }),
        });
        const data = await res.json();
        if (!data?.success) throw new Error(data?.error || 'Update failed');
      } else {
        const res = await fetch(`${API_URL}/users/login/roles/create/`, {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ name, description, capabilities: {} }),
        });
        const data = await res.json();
        if (!data?.success) throw new Error(data?.error || 'Create failed');
        roleId = data.data?.role_id;
      }

      // Resolve selected codes to ids. Skip any unknown (shouldn't happen if seeded).
      const codes = levelsToCodes(levels);
      const ids = codes.map((c) => codeToId[c]).filter(Boolean);
      const missing = codes.filter((c) => !codeToId[c]);
      if (missing.length) {
        showToast(
          `These permission codes are missing in the system: ${missing.join(', ')}. Ask an admin to (re)seed them.`,
          'error'
        );
      }

      const permRes = await fetch(`${API_URL}/users/login/roles/${roleId}/permissions/`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ permission_ids: ids }),
      });
      const permData = await permRes.json();
      if (!permData?.success) throw new Error(permData?.error || 'Saving permissions failed');

      showToast(isEdit ? 'Role updated' : 'Role created', 'success');
      onSaved();
    } catch (e) {
      showToast(String(e?.message || e), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      wide
      title={isEdit ? `Edit Role · ${role.name}` : 'Create New Role'}
      onClose={onClose}
      footer={
        <>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={saving}>
            <Save size={15} /> {saving ? 'Saving…' : 'Save Role'}
          </Btn>
        </>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Role Name" required>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Manager"
          />
        </Field>
        <Field label="Short Description">
          <TextInput
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this role do?"
          />
        </Field>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-slate-900 mb-1">Module Access</h4>
        <p className="text-xs text-slate-500 mb-3">
          Pick what this role can do in each module.
        </p>

        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2.5 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <div className="col-span-4">Module</div>
            <div className="col-span-8">Access Level</div>
          </div>
          {MODULES.map((m) => (
            <div key={m.key} className="grid grid-cols-12 items-center px-4 py-3 border-t border-slate-100">
              <div className="col-span-4">
                <div className="text-sm font-medium text-slate-900">{m.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{m.description}</div>
              </div>
              <div className="col-span-8 flex flex-wrap gap-2">
                {m.levels.map((lvl) => {
                  const active = levels[m.key] === lvl.value;
                  return (
                    <button
                      key={lvl.value}
                      type="button"
                      onClick={() => setLevel(m.key, lvl.value)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                      }`}
                    >
                      {lvl.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}
